import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Flex, Select, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import type { ValueInputProps } from '@pushly/aqe'
import { useSegmentValueInputContext } from './segment-value-input-context'
import type { NotificationOption } from './notification-source'
import { emitTermSet, readTermSet } from './term-set-value'

/** How long typing settles before it is treated as a search. */
const SEARCH_DEBOUNCE_MS = 320

/**
 * The fewest characters a search is worth making on.
 *
 * A notification list is large and an id or title fragment of three characters or
 * fewer matches too much to be useful, so the reader is left to narrow further before
 * anything is asked of the server. Strictly more than three, the way legacy gated it.
 */
const MIN_SEARCH_LENGTH = 3

const TOOLTIP =
    'Subscribers who clicked within the last 24 hours may not yet be properly attributed to segments using this condition.'

/** What the empty list means before the search gate is crossed, in the reader's terms. */
const SEARCH_HINT = `Type at least ${MIN_SEARCH_LENGTH + 1} characters to search`

/** What the empty list means once a search has actually been made and come back empty. */
const NO_MATCHES = 'No notifications match that search'

/**
 * What the empty list means where the search could not be made at all.
 *
 * Distinct from `NO_MATCHES` on purpose: telling the reader nothing matched when the
 * read failed sends them typing for a notification that is there, and hides an outage
 * behind what looks like an ordinary answer.
 */
const SEARCH_FAILED = 'Notification search is unavailable right now'

/** The stored value as the list of ids on screen, whatever shape it arrives in. */
function readSelectedIds(value: string | string[] | number | null | undefined): string[] {
    return readTermSet(value).map(String)
}

/**
 * How a notification reads wherever one is named — in this control's list and on the collapsed row
 * summarising it, which have to agree or the row would rename what the reader just picked.
 *
 * The id leads because it is what the condition stores and what the reader can always be shown; the
 * title follows where one is known. An id whose title has not resolved — or has resolved to nothing
 * the account can state — reads as itself rather than trailing an empty separator.
 */
export function describeNotification(id: string, title: string | undefined): string {
    return title === undefined || title === '' ? `#${id}` : `#${id}: ${title}`
}

/**
 * The notifications a `behaviors.notification_ids_clicked` condition matches clicks on.
 *
 * The condition means "clicked any of these", so this is a multi-entry search box: it resolves
 * the stored ids back to their titles and lets the reader add or drop notifications within the
 * one condition, rather than leaving them to hand-build an ORed row per notification. Reads
 * serve it and each owns its own abort slot: one read per stored id, made regardless of the
 * id's length, and the reader's typeahead, which is made only once the search is worth it and
 * is abandoned the moment a newer one supersedes it — a stale answer must never land on the
 * list. The row's own select owns the `a_contains`/`a_not_contains` operator, so none is
 * rendered here.
 *
 * One selected notification is emitted as the bare scalar every stored condition already holds,
 * which those two operators' clauses wrap into a one-entry set on every backend; two or more are
 * emitted as the list. Which of the two applies is `emitTermSet`'s answer from the operator, not
 * this renderer's, so the row cannot emit a scalar under an operator that cannot read one.
 */
export function NotificationIdClickedInput({
    value,
    onChange,
    operator,
    disabled,
    error,
    size,
    getPopupContainer,
}: ValueInputProps<string | string[] | number | null>) {
    const { notifications, recordNotificationTitles } = useSegmentValueInputContext()

    const selectId = `${useId()}-notification`

    // The ids on screen, held to one identity per membership: the value arrives as a fresh
    // array on every render, and the reads below must be keyed on which ids are selected
    // rather than on how many times the row re-rendered.
    const selectedKey = readSelectedIds(value).join(',')
    const selectedIds = useMemo(() => (selectedKey === '' ? [] : selectedKey.split(',')), [selectedKey])

    // The stored ids resolved to their options, and the current search's results. Kept apart
    // so emptying the search returns the list to the resolved values alone rather than blanking
    // the row the reader came here to keep.
    const [resolved, setResolved] = useState<Record<string, NotificationOption>>({})
    const [results, setResults] = useState<NotificationOption[]>([])
    const [searching, setSearching] = useState(false)
    /** Whether the term on screen is long enough to have been searched on. */
    /**
     * What is currently typed in the search box.
     *
     * Controlled so a selection can clear the text without clearing the results it produced — the
     * two are separate concerns that antd's `autoClearSearchValue` conflates.
     */
    const [searchTerm, setSearchTerm] = useState('')
    const [searchable, setSearchable] = useState(false)
    /** Whether the last search on the term on screen could not be made at all. */
    const [failed, setFailed] = useState(false)

    // A slot each, because a read is only ever abandoned by whoever started it: the component
    // owns the resolution reads — one per id, all of which may legitimately be open at once —
    // and the reader owns the search.
    const inFlight = useRef<{ resolutions: Set<AbortController>; search: AbortController | null }>({
        resolutions: new Set(),
        search: null,
    })
    const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
    /** The ids resolution has already been asked for, so a re-render does not re-ask. */
    const resolvedFor = useRef<Set<string>>(new Set())

    const latestOnChange = useRef(onChange)
    latestOnChange.current = onChange

    const latestRecordTitles = useRef(recordNotificationTitles)
    latestRecordTitles.current = recordNotificationTitles

    // Every title this row resolved is published for the rest of the builder to read. A collapsed
    // row shows what its condition targets without mounting this control, so it has no list of its
    // own to resolve from and would otherwise be left with bare ids for the whole session.
    //
    // Published off the resolved map rather than at each read, so one statement covers both ways a
    // title arrives: a stored id resolving, and the reader picking a search result. A title that
    // came back empty is not published — an id is the better reading of a notification whose title
    // nothing can state.
    useEffect(() => {
        const titles = Object.entries(resolved)
            .filter(([, option]) => option.title !== '')
            .map(([id, option]) => [id, option.title] as const)

        if (titles.length > 0) latestRecordTitles.current?.(Object.fromEntries(titles))
    }, [resolved])

    // Each stored id is resolved to its title on mount, and any id the row gains later is
    // resolved as it arrives. These reads ignore the length gate — an id the reader never
    // typed still has to resolve, however short.
    useEffect(() => {
        if (!notifications) return

        for (const id of selectedIds) {
            if (resolvedFor.current.has(id)) continue

            resolvedFor.current.add(id)
            const controller = new AbortController()
            inFlight.current.resolutions.add(controller)

            notifications
                .search(id, controller.signal)
                .then((options) => {
                    if (controller.signal.aborted) return

                    const match = options.find((option) => option.id === id)
                    if (match) setResolved((current) => ({ ...current, [id]: match }))
                })
                .catch(() => {
                    // A resolution that failed leaves the entry showing its bare id rather
                    // than taking the builder down; the service reports its own failures.
                })
                .finally(() => {
                    inFlight.current.resolutions.delete(controller)
                })
        }
    }, [notifications, selectedIds])

    useEffect(
        () => () => {
            if (debounce.current) clearTimeout(debounce.current)
            for (const controller of inFlight.current.resolutions) controller.abort()
            inFlight.current.search?.abort()
        },
        [],
    )

    const runSearch = useCallback(
        (term: string) => {
            if (!notifications) return

            inFlight.current.search?.abort()
            const controller = new AbortController()
            inFlight.current.search = controller
            setSearching(true)

            notifications
                .search(term, controller.signal)
                .then((options) => {
                    if (controller.signal.aborted) return
                    setFailed(false)
                    setResults(options)
                })
                .catch((error) => {
                    if (controller.signal.aborted) return

                    // A search that failed leaves the list as it was rather than blanking
                    // what the reader could already see, and says so where the list is
                    // empty: an empty list alone would read as "nothing matched", which is
                    // a different answer and one the reader would act on differently.
                    setFailed(true)
                    console.error('The notification search could not be made.', error)
                })
                .finally(() => {
                    if (inFlight.current.search === controller) {
                        inFlight.current.search = null
                        setSearching(false)
                    }
                })
        },
        [notifications],
    )

    const handleSearch = useCallback(
        (term: string) => {
            setSearchTerm(term)
            if (debounce.current) clearTimeout(debounce.current)

            const trimmed = term.trim()
            setSearchable(trimmed.length > MIN_SEARCH_LENGTH)

            if (trimmed.length <= MIN_SEARCH_LENGTH) {
                // Too little to search on. Any search in flight is abandoned and its
                // results cleared, returning the list to the resolved values alone —
                // and releasing the connection the abandoned read was holding.
                inFlight.current.search?.abort()
                inFlight.current.search = null
                setResults([])
                setSearching(false)
                setFailed(false)
                return
            }

            debounce.current = setTimeout(() => runSearch(trimmed), SEARCH_DEBOUNCE_MS)
        },
        [runSearch],
    )

    const options = useMemo(() => {
        const byId = new Map<string, NotificationOption>()
        for (const id of selectedIds) {
            const option = resolved[id]
            if (option) byId.set(option.id, option)
        }
        for (const option of results) byId.set(option.id, option)

        const mapped = [...byId.values()].map((option) => ({
            value: option.id,
            label: describeNotification(option.id, option.title),
        }))

        // A selected id whose title has not resolved yet still renders as itself rather than
        // as a blank entry while its resolution read is on its way — or for good, where the
        // notification it names is no longer one the account can return.
        const unresolved = selectedIds
            .filter((id) => !byId.has(id))
            .map((id) => ({ value: id, label: describeNotification(id, undefined) }))

        return [...unresolved, ...mapped]
    }, [resolved, results, selectedIds])

    const handleChange = useCallback(
        (ids: string[]) => {
            // Hold the chosen options as resolved so their labels stick without a fresh
            // resolution read for ids we already have the titles for.
            const picked = results.filter((option) => ids.includes(option.id))
            if (picked.length > 0) {
                setResolved((current) => {
                    const next = { ...current }
                    for (const option of picked) {
                        next[option.id] = option
                        resolvedFor.current.add(option.id)
                    }
                    return next
                })
            }

            // Clear what was typed, but not the results it produced. antd keeps the term in the
            // box after a selection, so the word searched for sat beside the token it produced and
            // read as though it had been duplicated. Clearing the term alone keeps the reason the
            // term was held in the first place: the list stays open so several notifications can be
            // taken from one search without retyping it once per notification.
            setSearchTerm('')

            latestOnChange.current(emitTermSet(operator, ids))
        },
        [operator, results],
    )

    return (
        <Flex className="segment-notification-id-clicked" vertical gap={4}>
            {/*
             * The note qualifies what this condition matches, so it sits with the label rather
             * than beside the box. As a sibling of the label — never inside it — the select's
             * accessible name stays the label's word alone.
             */}
            <Flex gap={4} align="center">
                <label htmlFor={selectId}>Notification</label>
                <Tooltip title={TOOLTIP}>
                    <span aria-label={TOOLTIP}>
                        <InfoCircleOutlined aria-hidden="true" />
                    </span>
                </Tooltip>
            </Flex>
            <Select
                id={selectId}
                className="segment-notification-id-clicked__select"
                style={{ width: '100%' }}
                mode="multiple"
                showSearch={true}
                filterOption={false}
                // The term is controlled, which is what lets a selection clear the text without
                // clearing the results it produced: several notifications can be taken from one
                // search instead of the reader retyping the term once per notification.
                //
                // `autoClearSearchValue` is deliberately not set. A controlled `searchValue`
                // overrides antd's own clearing either way, so passing it would assert a behaviour
                // this component no longer depends on — verified by flipping it and seeing nothing
                // change.
                searchValue={searchTerm}
                placeholder="Search by ID or Title"
                loading={searching}
                value={selectedIds}
                options={options}
                onSearch={handleSearch}
                onChange={handleChange}
                // The list is a server search that only starts once the term is worth making a
                // read on, so an empty list before then is the gate rather than an empty account.
                // Naming the gate is what makes it discoverable; antd's bare "No data" does not.
                // A read that could not be made is named apart from one that matched nothing.
                notFoundContent={failed ? SEARCH_FAILED : searchable ? NO_MATCHES : SEARCH_HINT}
                disabled={disabled}
                size={size}
                status={error ? 'error' : undefined}
                getPopupContainer={getPopupContainer}
            />
        </Flex>
    )
}
